import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  accessLocalStorage,
  getPjsipTransports,
  updatePjsipTransports,
} from '../../../services';
import { isValidIPv4, isValidCidr, isValidIPv6, FORBIDDEN_IPS } from '../../../utils/network';

import '../../../libs/bulma.min.css';
import './PjsipTransports.css';

// CRUD dos transports do PJSIP (4 transports: udp/tcp/ws/wss).
//
// Campos editáveis:
//   - local_net (lista — obrigatória, ≥ 1 CIDR IPv4 sem duplicatas)
//   - external_media_address (opcional — vazio = não toca a linha)
//   - external_signaling_address (opcional — vazio = não toca a linha)
//
// Dropdown "Aplicar em" permite escolher 1 transport ou "Todos" (default).
//
// IMPORTANTE: transports só carregam no boot do Asterisk. Para aplicar,
// `core restart` em janela de manutenção (derruba chamadas).

const TARGET_OPTIONS = [
  { value: 'all', label: 'Todos os transports (UDP, TCP, WS, WSS)' },
  { value: 'udp_transport', label: 'UDP' },
  { value: 'tcp_transport', label: 'TCP' },
  { value: 'ws_transport', label: 'WS' },
  { value: 'wss_transport', label: 'WSS' },
];

const MAX_LOCAL_NETS = 32;

const shortName = (canonical) => {
  if (!canonical || canonical === 'all') return '';
  return canonical.replace(/_transport$/, '').toUpperCase();
};

function PjsipTransports() {
  const history = useHistory();

  const [phase, setPhase] = useState('loading'); // 'loading'|'edit'|'saving'|'saved'|'error'|'drift'
  const [transports, setTransports] = useState([]);
  const [synced, setSynced] = useState(true);
  const [etag, setEtag] = useState('');
  const [lastAudit, setLastAudit] = useState(null);

  const [target, setTarget] = useState('all');
  const [localNet, setLocalNet] = useState(['']);
  const [externalMediaAddress, setExternalMediaAddress] = useState('');
  const [externalSignalingAddress, setExternalSignalingAddress] = useState('');
  const [dirty, setDirty] = useState(false);

  // Banner de confirmação de troca de target com dirty.
  const [pendingTargetSwitch, setPendingTargetSwitch] = useState(null);

  const [errorMsg, setErrorMsg] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);
  const [noop, setNoop] = useState(false);

  // Validações --------------------------------------------------------------
  const validateLocalNetEntry = (v) => {
    if (typeof v !== 'string') return 'Inválido';
    const trimmed = v.trim();
    if (trimmed === '') return 'Campo vazio';
    if (isValidIPv6(trimmed.split('/')[0] || '')) return 'IPv6 não suportado';
    if (!isValidCidr(trimmed)) return 'CIDR IPv4 inválido';
    const ipPart = trimmed.split('/')[0];
    if (FORBIDDEN_IPS.has(ipPart)) return 'IP reservado não permitido';
    return null;
  };

  const validateScalar = (v) => {
    if (v === '' || v === undefined || v === null) return null; // opcional
    const trimmed = String(v).trim();
    if (isValidIPv6(trimmed)) return 'IPv6 não suportado';
    if (!isValidIPv4(trimmed)) return 'IPv4 inválido';
    if (FORBIDDEN_IPS.has(trimmed)) return 'IP reservado não permitido';
    return null;
  };

  const localNetErrors = useMemo(() => localNet.map(validateLocalNetEntry), [localNet]);
  const localNetDuplicateIdx = useMemo(() => {
    const seen = new Map();
    const dups = new Set();
    localNet.forEach((v, i) => {
      const k = String(v || '').trim();
      if (!k) return;
      if (seen.has(k)) dups.add(i);
      else seen.set(k, i);
    });
    return dups;
  }, [localNet]);
  const emaError = useMemo(() => validateScalar(externalMediaAddress), [externalMediaAddress]);
  const esaError = useMemo(() => validateScalar(externalSignalingAddress), [externalSignalingAddress]);

  const formValid = useMemo(() => {
    if (localNet.length === 0) return false;
    if (localNet.length > MAX_LOCAL_NETS) return false;
    if (localNetErrors.some((e) => e !== null)) return false;
    if (localNetDuplicateIdx.size > 0) return false;
    if (emaError) return false;
    if (esaError) return false;
    return true;
  }, [localNet, localNetErrors, localNetDuplicateIdx, emaError, esaError]);

  // Carga inicial -----------------------------------------------------------
  const populateFromTransport = useCallback((all, chosenTarget) => {
    let source = null;
    if (chosenTarget && chosenTarget !== 'all') {
      source = all.find((t) => t.name === chosenTarget && t.present);
    }
    if (!source) source = all.find((t) => t.present) || null;
    if (!source) return;
    const ln = Array.isArray(source.localNet) && source.localNet.length > 0
      ? source.localNet.slice() : [''];
    setLocalNet(ln);
    setExternalMediaAddress(source.externalMediaAddress || '');
    setExternalSignalingAddress(source.externalSignalingAddress || '');
    setDirty(false);
  }, []);

  const loadCurrent = useCallback(async () => {
    const resp = await getPjsipTransports();
    if (resp.status !== 200) {
      setPhase('error');
      setErrorMsg((resp.data && resp.data.message) || 'Falha ao ler configuração.');
      return;
    }
    const d = resp.data || {};
    const all = d.transports || [];
    setTransports(all);
    setSynced(!!d.synced);
    setEtag(d.etag || '');
    setLastAudit(d.lastAudit || null);
    populateFromTransport(all, target);
    setPhase('edit');
  }, [populateFromTransport, target]);

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    const { user } = dataUser;
    if (!user || user.role !== 'root' || user.idtenant !== 'vgs') {
      return history.push('/home');
    }
    await loadCurrent();
  }, [history, loadCurrent]);

  useEffect(() => { validateUserLogged(); }, [validateUserLogged]);

  // Handlers ---------------------------------------------------------------
  const markDirty = () => { setDirty(true); setNoop(false); setErrorMsg(''); setErrorDetails(null); };

  const onTargetChange = (e) => {
    const nv = e.target.value;
    if (dirty && nv !== target) {
      // Mostra banner de confirmação inline em vez de aplicar direto.
      setPendingTargetSwitch(nv);
      return;
    }
    setTarget(nv);
    populateFromTransport(transports, nv);
  };

  const confirmTargetSwitch = () => {
    const nv = pendingTargetSwitch;
    setPendingTargetSwitch(null);
    setTarget(nv);
    populateFromTransport(transports, nv);
  };
  const cancelTargetSwitch = () => setPendingTargetSwitch(null);

  const onLocalNetChange = (i, value) => {
    setLocalNet((prev) => prev.map((v, idx) => (idx === i ? value : v)));
    markDirty();
  };

  const onLocalNetAdd = () => {
    if (localNet.length >= MAX_LOCAL_NETS) return;
    setLocalNet((prev) => [...prev, '']);
    markDirty();
  };

  const onLocalNetRemove = (i) => {
    if (localNet.length <= 1) return;
    setLocalNet((prev) => prev.filter((_, idx) => idx !== i));
    markDirty();
  };

  const onEmaChange = (e) => { setExternalMediaAddress(e.target.value); markDirty(); };
  const onEsaChange = (e) => { setExternalSignalingAddress(e.target.value); markDirty(); };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formValid || phase === 'saving') return;
    setPhase('saving');
    setErrorMsg('');
    setErrorDetails(null);
    setNoop(false);
    const payload = {
      transport: target,
      localNet: localNet.map((s) => String(s).trim()),
      externalMediaAddress: externalMediaAddress.trim(),
      externalSignalingAddress: externalSignalingAddress.trim(),
      expectedEtag: etag,
    };
    const resp = await updatePjsipTransports(payload);
    if (resp.status === 200) {
      const d = resp.data || {};
      setTransports(d.transports || []);
      setSynced(!!d.synced);
      setEtag(d.etag || '');
      setLastAudit(d.lastAudit || null);
      setNoop(!!d.noop);
      setDirty(false);
      setPhase('saved');
      // Após salvar, re-popular o form com o estado pós-escrita (importante
      // se o usuário continuar editando).
      populateFromTransport(d.transports || [], target);
      return;
    }
    if (resp.status === 409) {
      setPhase('drift');
      setErrorMsg((resp.data && resp.data.message)
        || 'pjsip.conf foi modificado externamente — recarregue a página.');
      return;
    }
    setPhase('error');
    setErrorMsg((resp.data && resp.data.message) || 'Falha ao salvar.');
    if (resp.data && resp.data.details) setErrorDetails(resp.data.details);
  };

  const reloadFromServer = async () => {
    setPhase('loading');
    setErrorMsg('');
    setErrorDetails(null);
    setNoop(false);
    setDirty(false);
    await loadCurrent();
  };

  // Render -----------------------------------------------------------------
  const renderScalarCell = (active, disabled, lastValue) => {
    if (active) return active;
    if (disabled && lastValue) {
      return (
        <span
          className="pjsip-disabled-value"
          title="Linha comentada com ';' — Asterisk ignora. Preencha o campo para reativar."
        >
          (desabilitada) <code>{lastValue}</code>
        </span>
      );
    }
    return '—';
  };

  const targetLabel = shortName(target);
  const attentionText = target === 'all'
    ? <>os valores serão gravados nos 4 transports (UDP, TCP, WS, WSS) simultaneamente. Mudanças só entram em vigor após restart completo.</>
    : <>os valores serão gravados apenas no transport <strong>{targetLabel}</strong>. Mudanças só entram em vigor após restart completo.</>;

  return (
    <div className="pjsip-container">
      <div className="pjsip-header">
        <div className="pjsip-title-wrapper">
          <svg className="pjsip-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <div>
            <h1 className="pjsip-title">Transporte Voz</h1>
            <p className="pjsip-subtitle">Informações de rede por onde os pacotes de voz devem fluir.</p>
          </div>
        </div>
      </div>

      <div className="pjsip-content">
        {phase === 'loading' && (
          <div className="alert-message alert-info">
            <div className="alert-body">Carregando configuração…</div>
          </div>
        )}

        {phase !== 'loading' && (
          <>
            <div className="alert-message alert-info">
              <div className="alert-body">
                <strong>Atenção</strong>: {attentionText}
              </div>
            </div>

            {!synced && (
              <div className="alert-message alert-warning">
                <div className="alert-body">
                  <strong>Transports divergentes:</strong> os 4 transports estão com valores diferentes hoje.
                </div>
              </div>
            )}

            {lastAudit && (
              <div className="alert-message alert-info">
                <div className="alert-body">
                  Última alteração: <strong>{new Date(lastAudit.createdAt).toLocaleString()}</strong>{' '}
                  por <code>{lastAudit.userEmail || '—'}</code> ({lastAudit.outcome})
                </div>
              </div>
            )}

            {phase === 'drift' && (
              <div className="alert-message alert-warning">
                <div className="alert-body">
                  <strong>Configuração alterada no servidor.</strong> {errorMsg}
                  <div style={{ marginTop: 12 }}>
                    <button className="btn-submit-pjsip" type="button" onClick={reloadFromServer}>
                      <span>Recarregar do servidor</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {phase === 'error' && errorMsg && (
              <div className="alert-message alert-danger">
                <div className="alert-body">
                  <strong>Erro:</strong> {errorMsg}
                  {errorDetails && errorDetails.issues && (
                    <ul style={{ marginTop: 8 }}>
                      {Object.entries(errorDetails.issues).map(([k, v]) => (
                        <li key={k}><code>{k}</code>: {typeof v === 'string' ? v : JSON.stringify(v)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {phase === 'saved' && noop && (
              <div className="alert-message alert-info">
                <div className="alert-body">
                  <strong>Sem alterações</strong> — os valores informados já estavam aplicados.
                </div>
              </div>
            )}

            {pendingTargetSwitch && (
              <div className="alert-message alert-warning">
                <div className="alert-body">
                  Você tem alterações não salvas. Trocar o escopo irá descartar.
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <button className="btn-submit-pjsip" type="button" onClick={confirmTargetSwitch}>
                      <span>Trocar mesmo assim</span>
                    </button>
                    <button className="btn-secondary-pjsip" type="button" onClick={cancelTargetSwitch}>
                      <span>Manter</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            <form className="pjsip-form" onSubmit={handleSubmit}>
              <div className="pjsip-field">
                <label htmlFor="pjsip-target"><strong>Aplicar em</strong></label>
                <select
                  id="pjsip-target"
                  className="pjsip-input"
                  value={target}
                  onChange={onTargetChange}
                  disabled={phase === 'saving' || phase === 'drift'}
                >
                  {TARGET_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="pjsip-field">
                <label>
                  <strong>Rede Local</strong> (CIDR IPv4 — ex: <code>147.93.71.203/24</code>)
                </label>
                <div className="pjsip-list">
                  {localNet.map((v, i) => {
                    const err = localNetErrors[i];
                    const isDup = localNetDuplicateIdx.has(i);
                    const cls = (v && (err || isDup)) ? 'pjsip-input-invalid' : 'pjsip-input';
                    return (
                      <div className="pjsip-list-row" key={`ln-${i}`}>
                        <input
                          type="text"
                          value={v}
                          onChange={(e) => onLocalNetChange(i, e.target.value)}
                          className={cls}
                          placeholder="200.174.142.40/24"
                          autoComplete="off"
                          disabled={phase === 'saving' || phase === 'drift'}
                        />
                        <button
                          type="button"
                          className="btn-list-remove"
                          aria-label="Remover"
                          onClick={() => onLocalNetRemove(i)}
                          disabled={localNet.length <= 1 || phase === 'saving' || phase === 'drift'}
                          title={localNet.length <= 1 ? 'Pelo menos 1 entrada é obrigatória' : 'Remover'}
                        >
                          ✕
                        </button>
                        {v && err && <span className="pjsip-field-error">{err}</span>}
                        {v && !err && isDup && <span className="pjsip-field-error">CIDR duplicado</span>}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    className="btn-list-add"
                    onClick={onLocalNetAdd}
                    disabled={localNet.length >= MAX_LOCAL_NETS || phase === 'saving' || phase === 'drift'}
                    title={localNet.length >= MAX_LOCAL_NETS ? `Limite de ${MAX_LOCAL_NETS}` : 'Adicionar Rede Local'}
                  >
                    + Adicionar Rede Local
                  </button>
                </div>
              </div>

              <div className="pjsip-field">
                <label htmlFor="externalMediaAddress">
                  <strong>IP Mídia</strong> (IPv4 — ex: <code>147.93.71.203</code>) — opcional
                </label>
                <input
                  id="externalMediaAddress"
                  type="text"
                  value={externalMediaAddress}
                  onChange={onEmaChange}
                  className={externalMediaAddress && emaError ? 'pjsip-input-invalid' : 'pjsip-input'}
                  placeholder="(em branco = desabilita a funcionalidade)"
                  autoComplete="off"
                  disabled={phase === 'saving' || phase === 'drift'}
                />
                {externalMediaAddress && emaError && (
                  <span className="pjsip-field-error">{emaError}</span>
                )}
              </div>

              <div className="pjsip-field">
                <label htmlFor="externalSignalingAddress">
                  <strong>IP Sinalização SIP</strong> (IPv4 — ex: <code>147.93.71.203</code>) — opcional
                </label>
                <input
                  id="externalSignalingAddress"
                  type="text"
                  value={externalSignalingAddress}
                  onChange={onEsaChange}
                  className={externalSignalingAddress && esaError ? 'pjsip-input-invalid' : 'pjsip-input'}
                  placeholder="(em branco = esabilita a funcionalidade)"
                  autoComplete="off"
                  disabled={phase === 'saving' || phase === 'drift'}
                />
                {externalSignalingAddress && esaError && (
                  <span className="pjsip-field-error">{esaError}</span>
                )}
              </div>

              <div className="pjsip-actions">
                <button
                  className="btn-submit-pjsip"
                  type="submit"
                  disabled={!formValid || phase === 'saving' || phase === 'drift'}
                  title={!formValid ? 'Verifique os campos obrigatórios e a validação' : 'Salvar'}
                >
                  <span>{phase === 'saving' ? 'Salvando…' : 'Salvar'}</span>
                </button>
                <button
                  className="btn-secondary-pjsip"
                  type="button"
                  onClick={reloadFromServer}
                  disabled={phase === 'saving'}
                >
                  <span>Recarregar</span>
                </button>
              </div>
            </form>

            <div className="pjsip-transports-table">
              <h2>Estado atual</h2>
              <table>
                <thead>
                  <tr>
                    <th>Transport</th>
                    <th>protocol</th>
                    <th>local_net</th>
                    <th>external_media_address</th>
                    <th>external_signaling_address</th>
                  </tr>
                </thead>
                <tbody>
                  {transports.map((t) => (
                    <tr key={t.name}>
                      <td><code>{t.name}</code></td>
                      <td>{t.protocol || '—'}</td>
                      <td>
                        {Array.isArray(t.localNet) && t.localNet.length > 0
                          ? t.localNet.map((v, i) => (
                              <div key={`ln-display-${t.name}-${i}`}><code>{v}</code></div>
                            ))
                          : '—'}
                      </td>
                      <td>{renderScalarCell(t.externalMediaAddress, t.externalMediaAddressDisabled, t.externalMediaAddressLastValue)}</td>
                      <td>{renderScalarCell(t.externalSignalingAddress, t.externalSignalingAddressDisabled, t.externalSignalingAddressLastValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {etag && (
                <div className="pjsip-etag">
                  ETag do arquivo: <code>{etag}</code>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PjsipTransports;
